import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { projectConstants } from '../../../../constants/project.constants';

import * as fileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ClinicService } from '../clinic/clinic.service';
import { map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { EInventoryExpiryStatus } from '../../../../models/inventory.model';

const EXCEL_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

export enum EToastActions {
  ERROR = 'ERROR',
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING'
}

@Injectable({
  providedIn: 'root',
})
export class UtilService {
  projectConstants = projectConstants;

  fullDays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  constructor(
    private titleService: Title,
    @Inject(DOCUMENT) private _document: Document,
    private router: Router,
    private location: Location,
    private clinicService: ClinicService,
    private toastr: ToastrService
  ) { }

  passwordsMustMatch(passwordKey: string, passwordConfirmationKey: string) {
    return (group: FormGroup) => {
      const passwordInput = group.controls[passwordKey],
        passwordConfirmationInput = group.controls[passwordConfirmationKey];
      if (passwordInput.value !== passwordConfirmationInput.value) {
        return passwordConfirmationInput.setErrors({ notEquivalent: true });
      } else {
        return passwordConfirmationInput.setErrors(null);
      }
    };
  }

  setDocTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  setDocFavicon(): void {
    const faviconId = 'appFavicon';
    const faviconDefaultPath = 'assets/img/brand';
    const favicon = projectConstants.favicon;
    this._document
      .getElementById(faviconId)
      .setAttribute('href', `${faviconDefaultPath}/${favicon}`);
  }

  createInitials(name): string {
    const firstLetter = name
      ? name?.split('')[0]
        ? name.split('')[0].charAt(0).toUpperCase()
        : ''
      : '';
    const secondLetter = name
      ? name?.split('')[1]
        ? name.split('')[1].charAt(0).toUpperCase()
        : ''
      : '';

    const initials = firstLetter + secondLetter;
    return initials;
  }

  formatDateToReturnDayOrMonthString(dateTimeStamp: string): string {
    const dt = new Date(dateTimeStamp),
      date = dt.getDate(),
      month = this.months[dt.getMonth()],
      timeDiff = new Date(dateTimeStamp).getDate() - Date.now(),
      diffDays = new Date().getDate() - date,
      diffMonths = new Date().getMonth() - dt.getMonth(),
      diffYears = new Date().getFullYear() - dt.getFullYear();

    if (diffYears === 0 && diffDays === 0 && diffMonths === 0) {
      return 'Today';
    } else if (diffYears === 0 && diffDays === 1) {
      return 'Yesterday';
    } else if (diffYears === 0 && diffDays === -1) {
      return 'Tomorrow';
    } else if (diffYears === 0 && diffDays < -1 && diffDays > -7) {
      return this.fullDays[dt.getDay()];
    } else if (diffYears >= 1) {
      return month + ' ' + date + ', ' + new Date(dateTimeStamp).getFullYear();
    } else {
      return month + ' ' + date;
    }
  }

  doesURLHaveText(text: string): boolean {
    const activeRoute = this.router.url;
    return activeRoute.includes(text) ? true : false;
  }

  goBack(): void {
    this.location.back();
  }

  public exportAsExcelFile(json: any[], excelFileName: string, headers?: any[]): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json, { header: headers });
    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ['data'],
    };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    fileSaver.saveAs(
      data,
      fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  public toDateString(date: Date): string {
    return (
      date.getFullYear().toString() +
      '-' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + date.getDate()).slice(-2) +
      'T' +
      date.toTimeString().slice(0, 5)
    );
  }

  createImageFromBlob(image: Blob) {
    let imageToShow: any;
    let reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        imageToShow = reader.result;
      },
      false
    );

    if (image) {
      reader.readAsDataURL(image);
    }

    return imageToShow;
  }

  isJSON(json): boolean {
    try {
      let parsedJSON = JSON.parse(json);

      if (!parsedJSON) {
        return false;
      }
    } catch (error) {
      return false;
    }
    return true;
  }

  getValue(obj: any, path: string): string {
    if (!path) return obj;
    if (!obj) return '';
    const properties = path.split('.');
    return this.getValue(obj[properties.shift()], properties.join('.'))
  }

  populateClinicField(keyNames: any[], formFieldsArray: FormlyFieldConfig[]): any[] {

    for (let index = 0; index < formFieldsArray.length; index++) {
      const formlyField = formFieldsArray[index];

      if (keyNames.includes(formlyField.key)) {
        formlyField.templateOptions.options = this.clinicService.getAllClinics().pipe(
          map(data => data.result)
        )
      }

      if (!formlyField.key && formlyField.fieldGroup) {
        this.populateClinicField(keyNames, formlyField.fieldGroup);
      }

      formFieldsArray[index] = formlyField;
    }

    return formFieldsArray;

  }

  calculateTimeBetweenDates(startDate: any, endDate: any): string {

    const startDateMilliseconds = new Date(startDate).getTime();
    const endDateMilliseconds = new Date(endDate).getTime();

    // get total seconds between the times
    let delta = Math.abs(endDateMilliseconds - startDateMilliseconds) / 1000;

    // calculate (and subtract) whole days
    let days = Math.floor(delta / 86400);
    delta -= days * 86400;

    // calculate (and subtract) whole hours
    let hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;

    // calculate (and subtract) whole minutes
    let minutes = Math.floor(delta / 60) % 60;
    delta -= minutes * 60;

    // what's left is seconds
    // let seconds = delta % 60;

    let elapsedTime = (days ? `${days} days ` : '') +
      (hours ? `${hours} hours ` : '') +
      (minutes ? `${minutes} minutes ` : '');
    // (seconds ? `${seconds} seconds ` : '');

    return elapsedTime;
  }

  triggerToast(type: EToastActions, message: string): void {
    switch (type) {
      case EToastActions.ERROR:
        this.toastr.error(message);
        break;

      case EToastActions.INFO:
        this.toastr.info(message);
        break;

      case EToastActions.SUCCESS:
        this.toastr.success(message);
        break;

      case EToastActions.WARNING:
        this.toastr.warning(message);
        break;

      default:
        break;
    }
  }

  // getAge function
  getAge(dateString) {
    // get today's date
    const today = new Date();
    // get user birth date
    const birthDate = new Date(dateString);
    // calculate user age
    // subtract their birth year from current year
    let age = today.getFullYear() - birthDate.getFullYear();
    // // subtract user birth month from current month
    const month = today.getMonth() - birthDate.getMonth();
    // if month <= 0
    // OR
    // today's date is less than user's birth date
    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
      // reduce user's age by 1
      age--;
    }
    // return user's current age
    return age;
  }

  getDaysBetweenDates(date1: Date, date2: Date) {
    date1 = new Date(date1);
    date2 = new Date(date2);

    // To calculate the time difference of two dates
    let Difference_In_Time = date2.getTime() - date1.getTime();

    // To calculate the no. of days between two dates
    let Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24);
    return Difference_In_Days;
  }

  monthDiff(d1: Date, d2: Date) {
    let expiryDateAndStatus: {
      text: string;
      status: string;
    };
    d1 = new Date(d1);
    d2 = new Date(d2);
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();

    if (months > 0) {
      expiryDateAndStatus = months > 3 ?
        {
          text: `Expires in ${months} months`,
          status: EInventoryExpiryStatus.MORETHAN3MONTHS
        } :
        {
          text: `Expires in ${months} months`,
          status: EInventoryExpiryStatus.WITHIN3MONTHS
        };
    } else {
      let numberOfDays = Math.floor(this.getDaysBetweenDates(d1, d2));
      expiryDateAndStatus = numberOfDays > 0 ?
        {
          text: `Expires in ${numberOfDays} days`,
          status: EInventoryExpiryStatus.WITHIN3MONTHS
        } :
        {
          text: `Expired`,
          status: EInventoryExpiryStatus.EXPIRED
        };
    }
    return expiryDateAndStatus;
  }

}
